import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalController, PopoverController } from '@ionic/angular';
import * as dayjs from 'dayjs';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { GlobalConstants } from 'src/app/constant/constant';
import { TimeRecordConstants } from 'src/app/constant/time-record-constants';
import { Activity } from 'src/app/entities/Activity';
import { WorkAssignment } from 'src/app/entities/WorkAssignment';
import { TimeRecord } from 'src/app/entities/timeRecord';
import { TimeFormControls } from 'src/app/models/form-groups/time-form.model';
import { FormControlParams } from 'src/app/models/forms/form-control-params';
import { AppUserAssignmentService } from 'src/app/services/app-user-assignment.service';
import { AppUserService } from 'src/app/services/app-user.service';
import { AssignmentService } from 'src/app/services/assignment.service';
import { FIPSStateService } from 'src/app/services/fips-state.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { TimeService } from 'src/app/services/time.service';
import { TimeRecordService } from 'src/app/services/timeRecord.service';
import { TooltipComponent } from 'src/app/shared/common/tooltip/tooltip.component';
import { DateTimeHelper } from 'src/app/utils/date-time-helper';
import { TimeEntryIntervalResolverService } from 'src/app/services/time-entry-interval-resolver.service';
import { EditNotableActivityComponent } from '../notable-activity/edit-notable-activity/edit-notable-activity.component';
import { ClockOutComponent } from './clock-out/clock-out.component';
import { EditTimeComponent } from './edit-time/edit-time.component';

/** 
 * WARNING: These values must stay in sync with TimeFormControls in models/form-groups/time-form.model.ts
 * @todo Consolidate these definitions to avoid duplication
 */ 
export interface TimeFormControlsValues {
  userId: number;
  isRestBreak: boolean;
  selectedDate: string;
  startDateTime: string;
  startTimeZoneId: string;
  endDateTime: string;
  endTimeZoneId: string;
  midnightSelectedAsEndDateTime: boolean;
  projectId: number;
  laborCategoryId: number;
  taskId: number;
  fipsStateId: number;
}
export interface TimeFormDataValues extends TimeFormControlsValues {
  isValid: boolean;
}

export interface TimeFormControlParams {
  date?: FormControlParams<string>;
  startTime?: FormControlParams<string>;
  endTime?: FormControlParams<string>;
  isRestBreakEnd?: boolean;
  isMealBreakEnd?: boolean;
  assignmentIsHidden?: boolean;
  showClockOutButton?: boolean;
  showBreakTypeButtons?: boolean;
  ignoreOverlappingRecords?: boolean;
  timeRecordToEditRecordId?: number;
  enclosingTimeRecordRecordId?: number;
}

@Component({
  selector: 'app-time',
  templateUrl: './time.component.html',
  styleUrls: ['./time.component.scss'],
})
export class TimeComponent implements OnInit, OnChanges, OnDestroy {
  @Input() userId: number;
  @Input() interval: number;
  @Input() timeFormControlParams: TimeFormControlParams;
  @Output() formValuesChanged = new EventEmitter<TimeFormDataValues>();
  @Output() intervalChanged = new EventEmitter<number>();
  assignment: WorkAssignment;
  isAssignmentValid: boolean;
  durationInMinutes: number;
  duration: string;
  overlappingTimeRecords: TimeRecord[] = [];
  orphanedActivities: Activity[] = [];
  currentTimeZoneId: string;
  isProjectTaskSelectionComponentLoaded = false;
  effectiveInterval: number = GlobalConstants.DEFAULT_TIMECLOCK_INTERVAL_MINUTES;

  formGroup: FormGroup<TimeFormControls>;
  isPageReady = false;

  timeRecordToEdit?: TimeRecord;

  private subscriptions: Subscription[] = [];

  constructor(
    private timeRecordService: TimeRecordService,
    private appUserAssignmentService: AppUserAssignmentService,
    private appUserService: AppUserService,
    private assignmentService: AssignmentService,
    private fipsStateService: FIPSStateService,
    private popoverController: PopoverController,
    private modalController: ModalController,
    private timeService: TimeService,
    private notificationsService: NotificationsService,
    private timeEntryIntervalResolverService: TimeEntryIntervalResolverService
  ) {}

  async ngOnInit() {
    await this.loadData();
    this.effectiveInterval = this.interval ?? GlobalConstants.DEFAULT_TIMECLOCK_INTERVAL_MINUTES;

    this.formGroup = new FormGroup<TimeFormControls>({
      userId: new FormControl(this.userId, {
        validators: [Validators.required],
        updateOn: 'blur',
      }),
      isRestBreak: new FormControl(false, {
        validators: this.timeFormControlParams?.showBreakTypeButtons ? [Validators.required] : [],
        updateOn: 'blur',
      }),
      selectedDate: new FormControl(this.timeFormControlParams.date.initialValue, {
        validators: [Validators.required],
        updateOn: 'blur',
      }),
      startDateTime: new FormControl(this.timeFormControlParams.startTime.initialValue, {
        validators: [Validators.required],
        updateOn: 'blur',
      }),
      startTimeZoneId: new FormControl(this.timeRecordToEdit?.ClockInTimeZoneId ?? this.currentTimeZoneId, {
        validators: [Validators.required],
        updateOn: 'blur',
      }),
      endDateTime: new FormControl(this.timeFormControlParams.endTime.initialValue, {
        validators: this.timeFormControlParams?.endTime.isHidden ? [] : [Validators.required],
        updateOn: 'blur',
      }),
      midnightSelectedAsEndDateTime: new FormControl(this.checkIsMidnightSelectedAsEndDateTime(this.timeFormControlParams.endTime.initialValue), { updateOn: 'blur' }),
      endTimeZoneId: new FormControl(this.timeRecordToEdit?.ClockOutTimeZoneId ?? (this.timeFormControlParams?.endTime.isHidden ? null : this.currentTimeZoneId), {
        validators: this.timeFormControlParams?.endTime.isHidden ? [] : [Validators.required],
        updateOn: 'blur',
      }),
      projectId: new FormControl(this.timeRecordToEdit?.ProjectId, {
        validators: [Validators.required],
        updateOn: 'blur',
      }),
      laborCategoryId: new FormControl(this.timeRecordToEdit?.LaborCategoryId, {
        validators: [Validators.required],
        updateOn: 'blur',
      }),
      taskId: new FormControl(this.timeRecordToEdit?.TaskId, {
        validators: [Validators.required],
        updateOn: 'blur',
      }),
      fipsStateId: new FormControl(this.timeRecordToEdit?.FIPSStateId, {
        validators: [Validators.required],
        updateOn: 'blur',
      })
    });

    const formGroupStatusSubscription = this.formGroup.statusChanges.subscribe(() => this.emitFormChanged());
    this.subscriptions.push(formGroupStatusSubscription);

    const fipsStateIdSubscription = this.formGroup.controls.fipsStateId.valueChanges.pipe(distinctUntilChanged()).subscribe(async (fipsStateId) => {
      await this.updateIntervalForFipsStateId(fipsStateId);
    });
    this.subscriptions.push(fipsStateIdSubscription);

    const formGroupValueSubscription = this.formGroup.valueChanges.subscribe(async () => {
      this.validateSelectedDate();
      this.validateStartAndEndTime();
      this.calculateDuration();

      if (!this.timeFormControlParams.ignoreOverlappingRecords) {
        this.loadOverlappingTimeRecords();
      }

      if (this.timeRecordToEdit) {
        this.loadOrpahnedActivities();
      }

      this.emitFormChanged();
    });
    this.subscriptions.push(formGroupValueSubscription);

    this.calculateDuration();
    this.emitFormChanged();

    await this.displayVerifiedTimeWarningIfNeeded();

    this.isPageReady = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.formGroup && changes.timeFormControlParams) {
      const prev = changes.timeFormControlParams.previousValue as TimeFormControlParams;
      const curr = changes.timeFormControlParams.currentValue as TimeFormControlParams;
      const startInitialChanged = prev?.startTime?.initialValue !== curr?.startTime?.initialValue;

      this.validateSelectedDate(false);
      this.validateStartAndEndTime(false);

      if (startInitialChanged && curr?.startTime?.initialValue) {
        // Bounds were recomputed for a new interval — reset to the freshly computed current time
        this.formGroup.patchValue({ startDateTime: curr.startTime.initialValue });
      } else {
        this.resnapSelectedTimesToEffectiveInterval();
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private async loadData() {
    await this.loadTimeRecordToEdit();
    await this.loadWorkAssignment();
    this.currentTimeZoneId = DateTimeHelper.getTimeZoneId();
  }

  private async loadTimeRecordToEdit() {
    if (this.timeFormControlParams.timeRecordToEditRecordId) {
      this.timeRecordToEdit = await this.timeRecordService.getTimeRecordByRecordId(this.timeFormControlParams.timeRecordToEditRecordId);
    }
  }

  private async loadWorkAssignment() {
    if (this.timeRecordToEdit) {
      this.assignment = await this.assignmentService.getWorkAssignmentFromTimeRecord(this.timeRecordToEdit);
    } else {
      this.assignment = this.appUserAssignmentService.currentAssignment;
    }
  }

  private async displayVerifiedTimeWarningIfNeeded() {
    const isVerified = await this.timeService.checkIsTimeVerifiedForDate(this.userId, this.formGroup.controls.selectedDate.value);
    if (isVerified) {
      this.notificationsService.showAlert({
        header: 'Warning',
        message: 'The date you are modifying has already been verified. Your supervisor will need to return your activity log to you before edits can be made.',
        buttons: [{ text: 'OK' }],
        suppression: {
          suppressKey: GlobalConstants.SUPPRESS_ALREADY_VERIFIED_ALERT_DATE,
          suppressDuration: 8,
          suppressLabel: 'Hide this message',
        },
      });
    }
  }

  emitFormChanged() {
    this.formValuesChanged.emit({
      ...(this.formGroup.value as TimeFormControlsValues),
      isValid: this.formGroup.valid && (this.timeFormControlParams.assignmentIsHidden || this.isAssignmentValid),
    });
  }

  async onSelectedDateChange(event: any) {
    this.updateStartAndEndDateTime();
    await this.displayVerifiedTimeWarningIfNeeded();
  }

  onStartTimeChange(event: string) {
    this.formGroup.patchValue({ startDateTime: event });
  }

  onEndTimeChange(event: string) {
    this.formGroup.patchValue({ endDateTime: event });
    const isMidnightSelected = this.checkIsMidnightSelectedAsEndDateTime(event);
    this.formGroup.patchValue({ midnightSelectedAsEndDateTime: isMidnightSelected });
  }

  private calculateDuration() {
    const startDateTime = this.formGroup.value.startDateTime;
    const startDateTimeZoneId = this.formGroup.value.startTimeZoneId;
    const endDateTime = this.formGroup.value.endDateTime;
    const endDateTimeZoneId = this.formGroup.value.endTimeZoneId;

    if (startDateTime && endDateTime && DateTimeHelper.checkTimeIsAfter(endDateTime, endDateTimeZoneId, startDateTime, startDateTimeZoneId, true)) {
      const duration = DateTimeHelper.getTimeDuration(startDateTime, startDateTimeZoneId, endDateTime, endDateTimeZoneId);
      this.duration = DateTimeHelper.formatDuration(duration, 'HHh MMm');
      this.durationInMinutes = duration.asMinutes();
    } else {
      this.duration = '';
      this.durationInMinutes = 0;
    }
  }

  private updateStartAndEndDateTime() {
    const date = this.formGroup.value.selectedDate;
    if (date) {
      // Ensure both startDateTime and endDateTime are set to the same date but keep the time
      const startDateTime = this.formGroup.value.startDateTime;
      const endDateTime = !this.timeFormControlParams.endTime ? dayjs() : this.formGroup.value.endDateTime;
      const currentDate = dayjs(date);

      if (startDateTime) {
        this.formGroup.patchValue({ startDateTime: currentDate.set('hour', dayjs(startDateTime).hour()).set('minute', dayjs(startDateTime).minute()).format('YYYY-MM-DDTHH:mm:00') });
      }

      if (endDateTime) {
        this.formGroup.patchValue({ endDateTime: currentDate.set('hour', dayjs(endDateTime).hour()).set('minute', dayjs(endDateTime).minute()).format('YYYY-MM-DDTHH:mm:00') });
      }
    }
  }

  private async loadOverlappingTimeRecords() {
    const date = this.formGroup.value.selectedDate;
    const startDateTime = this.formGroup.value.startDateTime;
    const startTimeZoneId = this.formGroup.value.startTimeZoneId;
    const endDateTime = this.timeFormControlParams.endTime.isHidden ? DateTimeHelper.getLocalNowString() : this.formGroup.value.endDateTime;
    const endTimeZoneId = this.formGroup.value.endTimeZoneId;

    if (date && startDateTime && endDateTime) {
      const newTimeRecord = new TimeRecord({
        EmployeeId: this.userId,
        ClockIn: startDateTime,
        ClockInTimeZoneId: startTimeZoneId,
        ClockOut: endDateTime,
        ClockOutTimeZoneId: endTimeZoneId,
      });
      const allOverlappingTimeRecords = await this.timeRecordService.getOverlappingTimeRecords(newTimeRecord);

      this.overlappingTimeRecords = this.timeFormControlParams.timeRecordToEditRecordId
        ? allOverlappingTimeRecords.filter(timeRecord => timeRecord.RecordId !== this.timeFormControlParams.timeRecordToEditRecordId)
        : allOverlappingTimeRecords;
    }

    if (this.overlappingTimeRecords.length > 0) {
      this.formGroup.setErrors({ overlappingRecords: true });
    } else {
      this.formGroup.setErrors(null);
    }
  }

  private async loadOrpahnedActivities() {
    const startDateTime = this.formGroup.value.startDateTime;
    const startTimeZoneId = this.formGroup.value.startTimeZoneId;
    const endDateTime = this.formGroup.value.endDateTime;
    const endTimeZoneId = this.formGroup.value.endTimeZoneId;

    if (startDateTime && endDateTime) {
      const getOrphanedNotableActivitiesData = {
        TimeRecordToEdit: this.timeRecordToEdit,
        ClockIn: startDateTime,
        ClockInTimeZoneId: startTimeZoneId,
        ClockOut: endDateTime,
        ClockOutTimeZoneId: endTimeZoneId,
      };
      this.orphanedActivities = await this.timeService.getOrphanedNotableActivities(getOrphanedNotableActivitiesData);
    }
  }

  async onOverlappingRecordClick(timeRecord: TimeRecord) {
    const modal = await this.modalController.create({
      component: EditTimeComponent,
      componentProps: {
        recordId: timeRecord.RecordId,
      },
      swipeToClose: true,
      backdropDismiss: false,
      cssClass: 'base-modal',
    });

    modal.onDidDismiss().then(async () => await this.loadOverlappingTimeRecords());

    await modal.present();
  }

  async onOrphanedActivityClick(activity: Activity) {
    const modal = await this.modalController.create({
      component: EditNotableActivityComponent,
      componentProps: {
        userId: this.userId,
        notableActivityToEditRecordId: activity.RecordId,
        interval: this.effectiveInterval,
      },
      swipeToClose: true,
      backdropDismiss: false,
      cssClass: 'base-modal',
    });

    modal.onDidDismiss().then(async () => await this.loadOrpahnedActivities());

    await modal.present();
  }

  async onClockOutButtonClick() {
    await this.modalController.dismiss();

    const modal = await this.modalController.create({
      component: ClockOutComponent,
      componentProps: {
        userId: this.userId,
        selectedDate: this.formGroup.value.selectedDate,
        interval: this.effectiveInterval,
        startDateTimeOverride: this.formGroup.value.startDateTime,
        startTimeZoneIdOverride: this.formGroup.value.startTimeZoneId,
      },
      swipeToClose: true,
      backdropDismiss: false,
      cssClass: 'base-modal',
    });

    await modal.present();
  }

  async onChangeAssignment(data: { assignment: WorkAssignment; isValid: boolean }) {
    this.formGroup.patchValue({
      projectId: data.assignment.ProjectId,
      laborCategoryId: data.assignment.LaborCategoryId,
      taskId: data.assignment.TaskId,
      fipsStateId: data.assignment.FIPSStateId,
    });
    this.isAssignmentValid = data.isValid;
    this.formGroup.updateValueAndValidity();
  }

  private async updateIntervalForFipsStateId(fipsStateId: number | null) {
    const defaultInterval = GlobalConstants.DEFAULT_TIMECLOCK_INTERVAL_MINUTES;
    const previousInterval = this.effectiveInterval;

    if (!fipsStateId) {
      this.effectiveInterval = defaultInterval;
    } else {
      try {
        const fipsState = await this.fipsStateService.getFIPSStateByOnlineId(fipsStateId, undefined, true);
        this.effectiveInterval = this.timeEntryIntervalResolverService.getIntervalForFipsState(
          this.appUserService.currentUser?.IsExempt,
          fipsState
        );
      } catch {
        this.effectiveInterval = defaultInterval;
      }
    }

    if (previousInterval !== this.effectiveInterval) {
      this.resnapSelectedTimesToEffectiveInterval();
      this.intervalChanged.emit(this.effectiveInterval);
    }
  }

  private resnapSelectedTimesToEffectiveInterval() {
    const selectedDate = this.formGroup.controls.selectedDate.value;
    const startTimeZoneId = this.formGroup.controls.startTimeZoneId.value;
    const endTimeZoneId = this.formGroup.controls.endTimeZoneId.value;

    const startMin = this.timeFormControlParams.startTime?.minValue
      ? DateTimeHelper.convertTimeToDatetime(this.timeFormControlParams.startTime.minValue, selectedDate, startTimeZoneId)
      : undefined;
    const startMax = this.timeFormControlParams.startTime?.maxValue
      ? DateTimeHelper.convertTimeToDatetime(this.timeFormControlParams.startTime.maxValue, selectedDate, startTimeZoneId)
      : undefined;
    const startDateTime = this.formGroup.controls.startDateTime.value
      ? dayjs(this.formGroup.controls.startDateTime.value)
      : undefined;

    if (startDateTime) {
      const snappedStart = this.snapDateTimeWithinBounds(startDateTime, startMin, startMax);
      this.formGroup.patchValue({ startDateTime: DateTimeHelper.formatDateTime(snappedStart) });
    }

    if (!this.timeFormControlParams.endTime?.isHidden && this.formGroup.controls.endDateTime.value) {
      const endMin = this.timeFormControlParams.endTime?.minValue
        ? DateTimeHelper.convertTimeToDatetime(this.timeFormControlParams.endTime.minValue, selectedDate, endTimeZoneId)
        : undefined;
      const endMax = this.timeFormControlParams.endTime?.maxValue
        ? DateTimeHelper.convertTimeToDatetime(this.timeFormControlParams.endTime.maxValue, selectedDate, endTimeZoneId)
        : undefined;
      const endDateTime = dayjs(this.formGroup.controls.endDateTime.value);
      const snappedEnd = this.snapDateTimeWithinBounds(endDateTime, endMin, endMax);
      this.formGroup.patchValue({ endDateTime: DateTimeHelper.formatDateTime(snappedEnd) });
    }
  }

  private snapDateTimeWithinBounds(value: dayjs.Dayjs, min?: dayjs.Dayjs, max?: dayjs.Dayjs): dayjs.Dayjs {
    let snapped = value.startOf('minute');

    if (this.effectiveInterval > 1) {
      const remainder = snapped.minute() % this.effectiveInterval;
      snapped = snapped.subtract(remainder, 'minute').startOf('minute');
    }

    if (min && snapped.isBefore(min)) {
      if (this.effectiveInterval <= 1) {
        snapped = min.startOf('minute');
      } else {
        const remainder = min.minute() % this.effectiveInterval;
        snapped = (remainder === 0 ? min : min.add(this.effectiveInterval - remainder, 'minute')).startOf('minute');
      }
    }

    if (max && snapped.isAfter(max)) {
      if (this.effectiveInterval <= 1) {
        snapped = max.startOf('minute');
      } else {
        const remainder = max.minute() % this.effectiveInterval;
        snapped = max.subtract(remainder, 'minute').startOf('minute');
      }
    }

    return snapped;
  }

  onChangeBreakType(event: any) {
    const value = event.detail.value === 'true';
    this.formGroup.patchValue({ isRestBreak: value });
  }

  private validateSelectedDate(emitEvent = true) {
    const date = this.formGroup.controls.selectedDate;

    if (this.formGroup.value.selectedDate) {
      if (this.timeFormControlParams.date.minValue && dayjs(date.value).isBefore(this.timeFormControlParams.date.minValue)) {
        date.setErrors({ dateBeforeMin: true }, { emitEvent });
      }
      if (this.timeFormControlParams.date.maxValue && dayjs(date.value).isAfter(this.timeFormControlParams.date.maxValue)) {
        date.setErrors({ dateAfterMax: true }, { emitEvent });
      }
    }
  }

  private validateStartAndEndTime(emitEvent = true) {
    const startDateTime = this.formGroup.controls.startDateTime;
    const endDateTime = this.formGroup.controls.endDateTime;
    const isRestBreak = this.formGroup.controls.isRestBreak;

    if (this.formGroup.value.selectedDate) {
      if (startDateTime.value) {
        if (this.timeFormControlParams.startTime.minValue) {
          const startTimeMinValueDatetime = DateTimeHelper.convertTimeToDatetime(this.timeFormControlParams.startTime.minValue, this.formGroup.value.selectedDate, this.formGroup.value.startTimeZoneId);

          if (DateTimeHelper.checkTimeIsBefore(startDateTime.value, this.formGroup.value.startTimeZoneId, startTimeMinValueDatetime, this.currentTimeZoneId)) {
            startDateTime.setErrors({ startDateTimeBeforeMin: true }, { emitEvent });
          }
        }

        if (this.timeFormControlParams.startTime.maxValue) {
          const startTimeMaxValueDatetime = DateTimeHelper.convertTimeToDatetime(this.timeFormControlParams.startTime.maxValue, this.formGroup.value.selectedDate, this.currentTimeZoneId);
          if (DateTimeHelper.checkTimeIsAfter(startDateTime.value, this.formGroup.value.startTimeZoneId, startTimeMaxValueDatetime, this.currentTimeZoneId)) {
            startDateTime.setErrors({ startDateTimeAfterMax: true }, { emitEvent });
          }
        }
      }

      if (endDateTime.value) {
        if (this.timeFormControlParams.endTime.minValue) {
          const endTimeMinValueDatetime = DateTimeHelper.convertTimeToDatetime(this.timeFormControlParams.endTime.minValue, this.formGroup.value.selectedDate, this.currentTimeZoneId);
          if (DateTimeHelper.checkTimeIsBefore(endDateTime.value, this.formGroup.value.endTimeZoneId, endTimeMinValueDatetime, this.currentTimeZoneId)) {
            endDateTime.setErrors({ endDateTimeBeforeMin: true }, { emitEvent });
          }
        }
        if (this.timeFormControlParams.endTime.maxValue && !this.timeFormControlParams.endTime.allowMidnight) {
          const endTimeMaxValueDatetime = DateTimeHelper.convertTimeToDatetime(this.timeFormControlParams.endTime.maxValue, this.formGroup.value.selectedDate, this.currentTimeZoneId);
          if (DateTimeHelper.checkTimeIsAfter(endDateTime.value, this.formGroup.value.endTimeZoneId, endTimeMaxValueDatetime, this.currentTimeZoneId)) {
            endDateTime.setErrors({ endDateTimeAfterMax: true }, { emitEvent });
          }
        }
      }

      if (!this.timeFormControlParams.timeRecordToEditRecordId && startDateTime.value && endDateTime.value) {
        const newTimeRecord = new TimeRecord({
          ClockIn: startDateTime.value,
          ClockOut: endDateTime.value,
          ClockInTimeZoneId: this.formGroup.value.startTimeZoneId,
          ClockOutTimeZoneId: this.formGroup.value.endTimeZoneId,
        });

        const isEndTimeAfterStartTime = this.timeRecordService.checkTimeRecordEndAfterStart(newTimeRecord);

        if (!isEndTimeAfterStartTime) {
          endDateTime.setErrors({ endDateTimeBeforeStartTime: true }, { emitEvent });
        } else if (isRestBreak.value && this.durationInMinutes !== TimeRecordConstants.REST_BREAK_DURATION_MINUTES) {
          endDateTime.setErrors({ invalidRestBreakDuration: true }, { emitEvent });
        } else {
          endDateTime.setErrors(null, { emitEvent });
        }
        this.formGroup.markAsTouched();
      }
    }
  }

  getValidationErrors(control: AbstractControl): string[] {
    if (!control.errors) {
      return [];
    }

    return Object.keys(control.errors)
      .map(key => {
        if (key === 'required' && !control.touched) {
          return ''; // Do not show required error if the control is not touched
        }

        switch (key) {
          case 'required':
            return 'This field is required';
          case 'endDateTimeBeforeStartTime':
            return 'End time must be after start time';
          case 'startDateTimeBeforeMin':
            return `Start time is before the earliest allowed time ${this.timeFormControlParams.startTime.minValue}`;
          case 'startDateTimeAfterMax':
            return `Start time is after the latest allowed time ${this.timeFormControlParams.startTime.maxValue}`;
          case 'endDateTimeBeforeMin':
            return `End time is before the earliest allowed time ${this.timeFormControlParams.endTime.minValue}`;
          case 'endDateTimeAfterMax':
            return `End time is after the latest allowed time ${this.timeFormControlParams.endTime.maxValue}`;
          case 'dateBeforeMin':
            return `Date is before the earliest allowed date ${this.timeFormControlParams.date.minValue}`;
          case 'dateAfterMax':
            return `Date is after the latest allowed date ${this.timeFormControlParams.date.maxValue}`;
          case 'invalidRestBreakDuration':
            return `Rest break must be exactly ${TimeRecordConstants.REST_BREAK_DURATION_MINUTES} minutes`;

            return `Rest break must be exactly ${TimeRecordConstants.REST_BREAK_DURATION_MINUTES} minutes`;
          default:
            return 'Unknown error';
        }
      })
      .filter(error => error !== '');
  }

  async displayDateTooltipIfNeeded(event: Event) {
    if (this.timeFormControlParams.timeRecordToEditRecordId) {
      const popover = await this.popoverController.create({
        component: TooltipComponent,
        componentProps: {
          text: 'Time records cannot be moved between dates. Please create a new time record for the date that you are trying to move to and delete this existing time record.',
        },
        translucent: false,
        event,
        showBackdrop: true,
      });

      await popover.present();
    }
  }

  checkIsMidnightSelectedAsEndDateTime(dateTime?: string): boolean {
    if (!dateTime) {
      return false;
    }
    const date = this.formGroup ? this.formGroup.value.selectedDate : this.timeFormControlParams.date.initialValue;
    const followingDay = DateTimeHelper.addTime(date, 1, 'day');
    const startOfFollowingDay = DateTimeHelper.getStartOfDay(followingDay);
    const timeZoneId = this.timeRecordToEdit?.ClockOutTimeZoneId ?? this.currentTimeZoneId;
    return DateTimeHelper.checkIsSame(startOfFollowingDay, this.currentTimeZoneId, dateTime, timeZoneId, 'minute');
  }
}
